import type { FoodSearchServing } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  Slider,
  SliderInput,
  SliderTracks,
  SliderValue,
  SliderValueInput,
  SliderValueLabel,
  SliderValueLabelContainer,
  Popover,
  PopoverTrigger,
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuItemSeparator,
} from "@tandemdiabetes/sugarmate-react-components"
import { Fragment } from "react"

import { defaultFoodSearchFood } from "./defaultFoodSearchFood"
import styles from "./ServingsRangeInput.module.scss"
import { usePolyglot } from "src/contexts"
import { useSliderInputProps } from "src/hooks"

interface Props {
  value: number;
  setValue: (n: number) => void;
  servings: FoodSearchServing[];
  currentServingIndex: number;
  setCurrentServingIndex: (n: number) => void;
}

const id = "food-servings-range-input"
const min = 0
const max = 5
const step = 0.1
const polyglotPrefix = "components.food_search."

/*
 * this component renders a range input for selecting the number of servings of
 * a food, this value is multiplied against the nutrition data to
 * fill in the nutrition info
 */
export function ServingsRangeInput(props: Props) {
  const {
    value,
    setValue,
    servings,
    currentServingIndex,
    setCurrentServingIndex,
  } = props

  const polyglot = usePolyglot()

  const {
    inputValue,
    sliderValue,
    onNumberChange,
    onSliderChange,
    onNumberBlur,
    onNumberKeyDown,
  } = useSliderInputProps({
    min,
    max,
    inputStep: step,
    sliderStep: step,
    value,
    onChange: setValue,
  })

  const currentServing =
    servings[currentServingIndex] ||
    servings[0] ||
    defaultFoodSearchFood.nutritional_servings[0]

  return (
    <div className={styles.rangeInputContainer}>
      <SliderValue sizePreset="largePopover">
        <SliderValueInput
          min={min}
          max={max}
          value={inputValue}
          step={step}
          onChange={onNumberChange}
          onBlur={onNumberBlur}
          onKeyDown={onNumberKeyDown}
        />
        <SliderValueLabelContainer>
          <Popover placement="top-start">
            <PopoverTrigger asChild>
              <SliderValueLabel
                htmlFor={id}
                clickable
              >
                {polyglot.t(
                  `${polyglotPrefix}serving`,
                  {
                    quantity: currentServing.serving_qty,
                    unitOfMeasurement: currentServing.serving_uom,
                  },
                )}
              </SliderValueLabel>
            </PopoverTrigger>
            <PopoverMenu aria-label={polyglot.t(`${polyglotPrefix}serving_aria_label`)}>
              {servings.map(
                (serving: FoodSearchServing, index: number) => {
                  const onClick = () => {
                    setCurrentServingIndex(index)
                  }

                  const key = `${serving.serving_qty} ${serving.serving_uom}`

                  return (
                    <Fragment key={key}>
                      {index !== 0 && (
                        <PopoverMenuItemSeparator />
                      )}
                      <PopoverMenuItem
                        onClick={onClick}
                        key={key}
                        autoDismiss
                      >
                        {polyglot.t(
                          `${polyglotPrefix}serving`,
                          {
                            quantity: serving.serving_qty,
                            unitOfMeasurement: serving.serving_uom,
                          },
                        )}
                      </PopoverMenuItem>
                    </Fragment>
                  )
                },
              )}
            </PopoverMenu>
          </Popover>
        </SliderValueLabelContainer>
      </SliderValue>
      <Slider className={styles.rangeInputSlider}>
        <SliderInput
          min={min}
          max={max}
          step={step}
          id={id}
          value={sliderValue ?? min}
          onChange={onSliderChange}
        />
        <SliderTracks
          min={min}
          max={max}
        />
      </Slider>
    </div>
  )
}
